import { Multistep, useMultistepApi } from 'informed';
import { type FC, useCallback, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';
import { Button } from '@/components/buttons';
import { FormGroup } from '@/components/form/groups/FormGroup';
import { ScFormRegistration } from '@/components/form/groups/styled';
import { Input } from '@/components/form/Input';
import { Select } from '@/components/form/Select';
import { IconComponent } from '@/components/IconComponent';
import { LoaderWrapper } from '@/components/profile/LoaderWrapper';
import { notEmpty } from '@/helpers/common';
import { ThemeMode } from '@/helpers/enums';
import { required } from '@/helpers/validators';
import { useCountryState } from '@/hooks';
import { useCountryOptions } from '@/hooks/common';
import { useStateOptions } from '@/modules/profile/hooks/useStateOptions';
import { useRegModalTheme } from '@/modules/registration/hooks/useRegModalTheme';
import { useGetCommonStore } from '@/store';
import {
  ScChangeCountryMessage,
  ScRegistrationWrapButtons,
} from '../../../styled';
import type { StepProps } from './type';

export const AddressInformationStep: FC<StepProps & { isLoading: boolean }> = ({
  step,
  isLoading,
}) => {
  const [selectedCountry, setSelectedCountry] = useState('');
  const { next, previous } = useMultistepApi();
  const { styleScheme, input, select, buttons } = useRegModalTheme();
  const useCommonStore = useGetCommonStore();

  const { t } = useTranslation();
  const { user, pendingUser } = useCommonStore(
    useShallow((state) => ({
      user: state.user,
      pendingUser: state.pendingUser,
    }))
  );

  const countryCode = useCommonStore.use.countryCode();

  const { states, statesPending, defaultState } = useCountryState(
    user?.country || selectedCountry || countryCode
  );

  const countryOptions = useCountryOptions();

  const { statesOptions, requiresState } = useStateOptions(states);

  const handleFinishClick = useCallback(() => {
    next();
  }, [next]);

  return (
    <Multistep.Step step={step}>
      <ScFormRegistration styleScheme={styleScheme}>
        <FormGroup
          styleScheme={styleScheme}
          title={
            select.withInsideLabel || select?.withoutLabel
              ? ''
              : t('common:auth.registration.form.country')
          }
        >
          {pendingUser ? null : (
            <Select
              disabled={notEmpty(user?.country)}
              initialValue={user?.country || countryCode}
              mode={ThemeMode.Light}
              name="country"
              onSelectOption={(state) => setSelectedCountry(String(state))}
              options={countryOptions}
              placeholder={t('common:auth.registration.form.country')}
              styleScheme={styleScheme}
              testId="address-info-country"
              validate={required}
              withInsideLabel={select.withInsideLabel}
            />
          )}
          {user?.country && (
            <ScChangeCountryMessage styleScheme={styleScheme}>
              <IconComponent className="tooltip-icon" name="tooltipIcon" />
              <p className="message">
                <Trans
                  components={{
                    1: <b id="intercomcustomLauncher" />,
                  }}
                  i18nKey="changeCountryMessage"
                />
              </p>
            </ScChangeCountryMessage>
          )}
        </FormGroup>
        <LoaderWrapper loading={statesPending}>
          {requiresState && (
            <FormGroup
              styleScheme={styleScheme}
              title={
                select.withInsideLabel || select.withoutLabel
                  ? ''
                  : t('common:auth.registration.form.state')
              }
            >
              <Select
                disabled={notEmpty(user?.state)}
                initialValue={user?.state || defaultState || undefined}
                mode={ThemeMode.Light}
                name="state"
                options={statesOptions}
                placeholder={t('common:auth.registration.form.state')}
                styleScheme={styleScheme}
                testId="address-info-state"
                validate={(value) =>
                  requiresState ? required(value, value) : undefined
                }
                withInsideLabel={
                  select?.withStateInsideLabel || select.withInsideLabel
                }
              />
            </FormGroup>
          )}
        </LoaderWrapper>
        <FormGroup
          styleScheme={styleScheme}
          title={
            input.withInsideLabel ? '' : t('common:auth.registration.form.city')
          }
        >
          <Input
            disabled={notEmpty(user?.city)}
            initialValue={user?.city}
            internalLabel={
              input.withInsideLabel ? t('auth.registration.form.city') : ''
            }
            name="city"
            placeholder={
              input.withInsideLabel
                ? ''
                : t('common:auth.registration.form.city')
            }
            styleScheme={styleScheme}
            testId="address-info-city"
            validate={required}
            validateOn="blur"
            withInsideLabel={input.withInsideLabel}
          />
        </FormGroup>
        <FormGroup
          styleScheme={styleScheme}
          title={
            input.withInsideLabel
              ? ''
              : t('common:auth.registration.form.address')
          }
        >
          <Input
            disabled={notEmpty(user?.address)}
            initialValue={user?.address}
            internalLabel={
              input.withInsideLabel ? t('auth.registration.form.address') : ''
            }
            name="address"
            placeholder={
              input.withInsideLabel
                ? ''
                : t('common:auth.registration.form.address')
            }
            styleScheme={styleScheme}
            testId="address-info-address"
            validate={required}
            validateOn="blur"
            withInsideLabel={input.withInsideLabel}
          />
        </FormGroup>
        <FormGroup
          styleScheme={styleScheme}
          title={
            input.withInsideLabel
              ? ''
              : t('common:auth.registration.form.postalCode')
          }
        >
          <Input
            disabled={notEmpty(user?.postalCode)}
            initialValue={user?.postalCode}
            internalLabel={
              input.withInsideLabel
                ? t('auth.registration.form.postalCode')
                : ''
            }
            name="postalCode"
            placeholder={
              input.withInsideLabel
                ? ''
                : t('common:auth.registration.form.postalCode')
            }
            styleScheme={styleScheme}
            testId="address-info-postal-code"
            validate={required}
            validateOn="blur"
            withInsideLabel={input.withInsideLabel}
          />
        </FormGroup>
      </ScFormRegistration>
      <ScRegistrationWrapButtons gap={buttons?.back.gap}>
        <Button
          {...buttons?.back.colorMode}
          large
          noMinWidth
          onClick={previous}
          testId="address-info-back-button"
          wide
        >
          {t('auth.registration.buttons.back')}
        </Button>
        <Button
          type="submit"
          {...buttons?.next.colorMode}
          isLoading={isLoading}
          large
          noMinWidth
          onClick={handleFinishClick}
          testId="address-info-submit-button"
          wide
        >
          {t('button.finish')}
        </Button>
      </ScRegistrationWrapButtons>
    </Multistep.Step>
  );
};
